import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePets } from '../context/PetContext';
import { generateLinkCode, sendCode, addCredentials, deleteAccount, unlinkMinecraft, getMe } from '../api/client';
import { appleSignInAvailable, prepareAppleSignIn, signInWithApple } from '../auth/appleSignIn';
import {
  PetlingsLogo,
  LeafIcon,
  ShieldIcon,
  CrownIcon,
  StarIcon,
  HeartIcon,
  KennelIcon,
  SyncIcon,
} from '../components/icons';
import TreatHistory from '../components/treats/TreatHistory';
import './Profile.css';

const TIER_CONFIG = {
  free: { label: 'Free', Icon: LeafIcon, color: 'var(--green-primary)', bg: '#E8F5E9' },
  plus: { label: 'Plus', Icon: ShieldIcon, color: '#1565C0', bg: '#E3F2FD' },
  premium: { label: 'Premium', Icon: CrownIcon, color: '#F57F17', bg: '#FFF8E1' },
};

const TYPE_LABELS = {
  cat: 'Cat',
  wolf: 'Wolf',
  fox: 'Fox',
  rabbit: 'Rabbit',
  parrot: 'Parrot',
  axolotl: 'Axolotl',
  panda: 'Panda',
  baby_dragon: 'Baby Dragon',
  slime: 'Slime',
};

export default function Profile() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading, logout } = useAuth();
  const { pets, syncStatus } = usePets();

  React.useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [authLoading, isAuthenticated, navigate]);

  const stats = useMemo(() => {
    if (!pets || pets.length === 0) {
      return { total: 0, highestLevel: 0, favoriteType: null };
    }

    const highestLevel = Math.max(...pets.map((p) => p.level));

    const typeCounts = {};
    pets.forEach((p) => {
      typeCounts[p.pet_type_id] = (typeCounts[p.pet_type_id] || 0) + 1;
    });
    const favoriteType = Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || null;

    return { total: pets.length, highestLevel, favoriteType };
  }, [pets]);

  if (authLoading) return <div className="profile-loading">Loading...</div>;
  if (!isAuthenticated || !user) return null;

  const tier = user.tier || 'free';
  const tierConfig = TIER_CONFIG[tier] || TIER_CONFIG.free;
  const TierIcon = tierConfig.Icon;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const hasRealMcLink = !!user.mojang_uuid;

  return (
    <div className="profile-page">
      <div className="container">
        <div className="profile-card card">
          {/* Header */}
          <div className="profile-header">
            <div className="profile-avatar">
              <PetlingsLogo size={36} color="var(--green-primary)" />
            </div>
            <div className="profile-header-info">
              <h1 className="profile-username">{user.minecraft_username || user.username}</h1>
              <div className="profile-tier-badge" style={{ background: tierConfig.bg }}>
                <TierIcon size={14} color={tierConfig.color} />
                <span style={{ color: tierConfig.color }}>{tierConfig.label}</span>
              </div>
            </div>
          </div>

          {/* Connection status */}
          <div className="profile-connection">
            <SyncIcon size={16} color={syncStatus === 'synced' ? 'var(--green-primary)' : syncStatus === 'error' ? '#C62828' : 'var(--text-muted)'} />
            <span className={`profile-status profile-status-${syncStatus === 'synced' ? 'online' : syncStatus === 'error' ? 'offline' : 'idle'}`}>
              {syncStatus === 'synced' && 'Connected'}
              {syncStatus === 'syncing' && 'Syncing...'}
              {syncStatus === 'error' && 'Offline'}
              {syncStatus === 'idle' && 'Idle'}
            </span>
            <span className={`profile-status-dot profile-dot-${syncStatus === 'synced' ? 'online' : syncStatus === 'error' ? 'offline' : 'idle'}`} />
          </div>

          {/* Pet collection stats */}
          <div className="profile-stats">
            <h3 className="profile-section-title">Pet Collection</h3>
            <div className="profile-stats-grid">
              <div className="profile-stat-item">
                <KennelIcon size={20} color="var(--green-dark)" />
                <div className="profile-stat-detail">
                  <span className="profile-stat-value">{stats.total}</span>
                  <span className="profile-stat-label">Total Pets</span>
                </div>
              </div>
              <div className="profile-stat-item">
                <StarIcon size={20} color="var(--yellow-warm)" />
                <div className="profile-stat-detail">
                  <span className="profile-stat-value">{stats.highestLevel}</span>
                  <span className="profile-stat-label">Highest Level</span>
                </div>
              </div>
              <div className="profile-stat-item">
                <HeartIcon size={20} color="#E57373" />
                <div className="profile-stat-detail">
                  <span className="profile-stat-value">
                    {stats.favoriteType ? (TYPE_LABELS[stats.favoriteType] || stats.favoriteType) : '--'}
                  </span>
                  <span className="profile-stat-label">Favorite Type</span>
                </div>
              </div>
            </div>
          </div>

          {/* Treat history */}
          <TreatHistory limit={10} />

          {/* Link Minecraft Account */}
          <LinkMinecraftSection user={user} />

          {/* Add Email/Password for mod-only users */}
          <AddCredentialsSection user={user} />

          {/* Delete the account, as on the app */}
          <DeleteAccountSection onDeleted={() => { logout(); navigate('/login?deleted=1'); }} />

          {/* Actions */}
          <div className="profile-actions">
            <button className="btn btn-ghost profile-pets-btn" onClick={() => navigate('/pets')}>
              <KennelIcon size={16} /> View Kennel
            </button>
            <button className="btn profile-logout-btn" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DeleteAccountSection({ onDeleted }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [needsApple, setNeedsApple] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const applePrepared = React.useRef(null);

  const prepareApple = React.useCallback(() => {
    if (!appleSignInAvailable()) return;
    prepareAppleSignIn()
      .then((p) => { applePrepared.current = p; })
      .catch(() => { applePrepared.current = null; });
  }, []);

  const attempt = async (apple = null) => {
    setError('');
    setWorking(true);
    try {
      await deleteAccount(apple);
      onDeleted();
    } catch (err) {
      if (err.response?.status === 409) {
        setNeedsApple(true);
        prepareApple();
        setError('This account uses Sign in with Apple. Confirm with Apple to delete it.');
      } else {
        setError(err.response?.data?.detail || "Your account wasn't deleted. Please try again.");
      }
    } finally {
      setWorking(false);
    }
  };

  const confirmWithApple = async () => {
    const prepared = applePrepared.current;
    if (!prepared) {
      setError('Sign in with Apple is still loading. Please try again in a moment.');
      prepareApple();
      return;
    }
    try {
      const apple = await signInWithApple(prepared);
      if (apple) await attempt(apple);
    } catch {
      setError("Apple didn't confirm. Please try again.");
    } finally {
      prepareApple();
    }
  };

  if (!open) {
    return (
      <div className="profile-section profile-delete">
        <button className="profile-delete-link" onClick={() => setOpen(true)} data-testid="delete-account">
          Delete account…
        </button>
      </div>
    );
  }

  return (
    <div className="profile-section profile-delete card">
      <h3 className="profile-section-title">Delete account</h3>
      <p>This deletes, for good: your pets and their gardens, everything they learned about you, every
        conversation, your treats, cosmetics and history, and your account. It can't be undone, and
        purchases aren't refunded. Your Minecraft account is not deleted, only its link to Petlings.
        If you signed in with Apple, Petlings is removed from your Apple ID too.</p>
      {error && <p className="profile-delete-error">{error}</p>}
      {needsApple ? (
        <button className="btn profile-delete-btn" onClick={confirmWithApple} disabled={working}>
          Confirm with Apple and delete
        </button>
      ) : (
        <>
          <label className="profile-delete-label">
            Type <strong>DELETE</strong> to confirm
            <input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
          </label>
          <button className="btn profile-delete-btn" disabled={typed !== 'DELETE' || working}
                  onClick={() => attempt()}>
            {working ? 'Deleting…' : 'Delete forever'}
          </button>
        </>
      )}
      <button className="profile-delete-link" onClick={() => setOpen(false)}>Keep my account</button>
    </div>
  );
}

/**
 * Unlinking, said plainly: you keep everything, the Minecraft account starts
 * fresh. The password (or Apple) proves it is the person who keeps it all.
 * The server ends every other login and hands back a new one for this page.
 */
function UnlinkMinecraft({ name }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [needsApple, setNeedsApple] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const applePrepared = React.useRef(null);

  const prepareApple = React.useCallback(() => {
    if (!appleSignInAvailable()) return;
    prepareAppleSignIn()
      .then((p) => { applePrepared.current = p; })
      .catch(() => { applePrepared.current = null; });
  }, []);

  const attempt = async (apple = null) => {
    setError('');
    setWorking(true);
    try {
      const res = await unlinkMinecraft(apple ? { apple } : { password });
      localStorage.setItem('petlings_token', res.data.access_token);
      const me = await getMe();
      localStorage.setItem('petlings_user', JSON.stringify(me.data));
      window.location.reload();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && typeof detail === 'string' && detail.includes('Apple')) {
        setNeedsApple(true);
        prepareApple();
      } else {
        setError(detail || 'Something went wrong. Please try again.');
      }
    }
    setWorking(false);
  };

  const confirmWithApple = async () => {
    const prepared = applePrepared.current;
    if (!prepared) { setError('Sign in with Apple is still loading. Please try again in a moment.'); prepareApple(); return; }
    try {
      const apple = await signInWithApple(prepared);
      if (apple) await attempt(apple);
    } catch {
      setError("Apple didn't confirm. Please try again.");
    } finally {
      prepareApple();
    }
  };

  if (!open) {
    return (
      <button className="profile-delete-link" onClick={() => setOpen(true)} data-testid="unlink-minecraft">
        Unlink this Minecraft account…
      </button>
    );
  }
  return (
    <div className="profile-unlink">
      <ul className="profile-unlink-points">
        <li>You keep your pets, treats, items, purchases and gardens.</li>
        <li><strong>{name}</strong> starts fresh the next time it plays Petlings in Minecraft.</li>
        <li>You'll be signed out on your other devices and in the game.</li>
        <li>You can link a Minecraft account again any time; unlinking works once a month.</li>
      </ul>
      {needsApple ? (
        <button className="btn btn-secondary" onClick={confirmWithApple} disabled={working}>Confirm with Apple</button>
      ) : (
        <div className="profile-unlink-row">
          <input type="password" className="input" placeholder="Your Petlings password" value={password}
                 onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
          <button className="btn btn-secondary" onClick={() => attempt()} disabled={!password || working}>
            {working ? 'Unlinking…' : `Unlink ${name}`}
          </button>
        </div>
      )}
      <button className="profile-delete-link" onClick={() => setOpen(false)}>Cancel</button>
      {error && <p className="profile-error">{error}</p>}
    </div>
  );
}

function LinkMinecraftSection({ user }) {
  const [linkCode, setLinkCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const isLinked = !!user.mojang_uuid;

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await generateLinkCode();
      setLinkCode(res.data.code);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate code');
    }
    setLoading(false);
  };

  const handleCopy = () => {
    if (linkCode) {
      navigator.clipboard.writeText(linkCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="profile-link-section">
      <h3 className="profile-section-title">Minecraft Account</h3>
      {isLinked ? (
        <>
          <div className="profile-link-status profile-link-connected">
            <span className="profile-link-dot" />
            <span>Linked to <strong>{user.mojang_username}</strong></span>
            <span className="profile-link-uuid">{user.mojang_uuid}</span>
          </div>
          <UnlinkMinecraft name={user.mojang_username} />
        </>
      ) : (
        <div className="profile-link-unlinked">
          <p className="profile-link-desc">
            Link your Minecraft account to sync pets, cosmetics, and chat between the mod and this website.
          </p>
          {!linkCode ? (
            <button className="btn btn-secondary" onClick={handleGenerate} disabled={loading}>
              {loading ? 'Generating...' : 'Generate Link Code'}
            </button>
          ) : (
            <div className="profile-link-code-box">
              <p className="profile-link-instruction">
                Open the Petlings mod in Minecraft, go to <strong>Account</strong>, click <strong>Link Web Account</strong>, and enter this code:
              </p>
              <div className="profile-link-code" onClick={handleCopy}>
                <span className="profile-link-code-value">{linkCode}</span>
                <span className="profile-link-code-copy">{copied ? 'Copied!' : 'Click to copy'}</span>
              </div>
              <p className="profile-link-expires">Code expires in 5 minutes</p>
            </div>
          )}
          {error && <p className="profile-link-error">{error}</p>}
        </div>
      )}
    </div>
  );
}

function AddCredentialsSection({ user }) {
  const [step, setStep] = useState('form'); // 'form' | 'verify' | 'done'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const hasEmail = user.email && user.email.includes('@');

  if (hasEmail) {
    return (
      <div className="profile-link-section">
        <h3 className="profile-section-title">Email & Password</h3>
        <div className="profile-link-status profile-link-connected">
          <span className="profile-link-dot" />
          <span>{user.email}</span>
        </div>
      </div>
    );
  }

  const handleSendCode = async () => {
    if (!email.trim() || !password.trim()) { setError('Enter email and password.'); return; }
    if (password.length < 8) { setError('Use a password of at least 8 characters.'); return; }
    setLoading(true); setError('');
    try {
      await sendCode(email.trim(), 'verify');
      setStep('verify');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not send code.');
    }
    setLoading(false);
  };

  const handleVerify = async () => {
    if (!code.trim()) { setError('Enter the code.'); return; }
    setLoading(true); setError('');
    try {
      await addCredentials(email.trim(), password, code.trim());
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed.');
    }
    setLoading(false);
  };

  return (
    <div className="profile-link-section">
      <h3 className="profile-section-title">Email & Password</h3>
      {step === 'done' ? (
        <div className="profile-link-status profile-link-connected">
          <span className="profile-link-dot" />
          <span>Email linked! You can now sign in with <strong>{email}</strong></span>
        </div>
      ) : step === 'verify' ? (
        <div className="profile-link-unlinked">
          <p className="profile-link-desc">We sent a code to <strong>{email}</strong></p>
          <input type="text" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="ABC123" maxLength={6} className="profile-verify-code-input" />
          <br />
          <button className="btn btn-primary btn-sm" onClick={handleVerify} disabled={loading}>
            {loading ? 'Verifying...' : 'Verify & Link'}
          </button>
          {error && <p className="profile-link-error">{error}</p>}
        </div>
      ) : (
        <div className="profile-link-unlinked">
          <p className="profile-link-desc">
            Add an email and password so you can sign in from the web or iOS without the mod.
          </p>
          <div className="profile-cred-form">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com" className="profile-cred-input" />
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters" minLength={8} className="profile-cred-input" />
            <button className="btn btn-secondary btn-sm" onClick={handleSendCode} disabled={loading}>
              {loading ? 'Sending code...' : 'Send Verification Code'}
            </button>
          </div>
          {error && <p className="profile-link-error">{error}</p>}
        </div>
      )}
    </div>
  );
}
